/**
 * The site's URL shape, shared by the Worker, the client and the importer.
 *
 * Two levels. Site-level pages live at the root: `/` (the worlds), the pages
 * about the site, the account pages. Everything about a world lives under that
 * world's slug: `/discworld/series/city-watch`. The slug is `universes.slug`,
 * resolved against the database, never written into a link by hand, so a
 * second world needs no code.
 *
 * Because a world's slug is the first path segment, it shares that segment
 * with every site-level page. The importer refuses a universe whose slug is on
 * this list, and the Worker never treats one of these as a world.
 */

import { quizFor } from './quizzes/index.ts'

export const RESERVED_FIRST_SEGMENTS: ReadonlySet<string> = new Set([
  // Site pages, built or planned.
  'account',
  'register',
  'signin',
  'verify',
  'forgot-password',
  'reset-password',
  'contact',
  'terms',
  'privacy',
  'about',
  // The Worker, its build output, and Cloudflare's own edge paths.
  'api',
  'assets',
  'cdn-cgi',
  // Paths that redirect to the first world, because they were its URLs before
  // there were worlds. See src/worker/legacy-redirects.ts.
  'series',
  'books',
  'outside',
])

/**
 * Site pages that exist today. A reserved segment not on this list — `/contact`
 * until the contact page is built — is a 404, not a page that renders nothing.
 * Add a page here in the same change that builds it.
 */
export const BUILT_SITE_PAGES: ReadonlySet<string> = new Set([
  'account',
  'register',
  'signin',
  'verify',
  'forgot-password',
  'reset-password',
  'terms',
  'privacy',
  'contact',
  'about',
])

/**
 * Site pages a search engine may index: every built site page except the
 * account pages, which carry `noindex`. `/terms`, `/privacy`, `/contact` and
 * `/about` join the sitemap by being added to BUILT_SITE_PAGES, and to
 * nothing else.
 */
export const NOINDEX_SITE_PAGES: ReadonlySet<string> = new Set([
  'account',
  'register',
  'signin',
  'verify',
  'forgot-password',
  'reset-password',
])

/**
 * The sections every world has: `/:world/series`, `/:world/books`,
 * `/:world/outside`. A world with a quiz (src/shared/quizzes/) also has
 * `/:world/quiz`. Anything else under a world is a 404 from the Worker. The
 * sitemap and llms.txt list a world's pages from `worldSections`, so a page
 * cannot exist without being listed, or be listed and answer 404.
 */
export const WORLD_SECTIONS: ReadonlySet<string> = new Set(['series', 'books', 'outside'])

/** The sections world `world` has: every world's, and `quiz` where it has one. */
export function worldSections(world: string): string[] {
  return [...WORLD_SECTIONS, ...(quizFor(world) === null ? [] : ['quiz'])]
}

/** Whether a first path segment could name a world. */
export function couldBeWorld(segment: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(segment) && !RESERVED_FIRST_SEGMENTS.has(segment)
}

/** A path inside a world: `worldPath('discworld', 'books', 'mort')` is `/discworld/books/mort`. */
export function worldPath(world: string, ...parts: string[]): string {
  return ['', world, ...parts].join('/')
}
